import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Rocket, CheckCircle, AlertCircle, Clock } from 'lucide-react';

interface Stats {
  sites: number;
  totalDeployments: number;
  activeDeployments: number;
}

interface Site {
  id: string;
  name: string;
  domain: string;
  status: string;
}

interface Deployment {
  id: string;
  status: string;
  triggeredBy: string;
  createdAt: string;
  site: {
    name: string;
    domain: string;
  };
}

export default function Dashboard() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [sites, setSites] = useState<Site[]>([]);
  const [deployments, setDeployments] = useState<Deployment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetch('/api/admin/stats').then((r) => r.json()),
      fetch('/api/sites').then((r) => r.json()),
      fetch('/api/deployments?limit=5').then((r) => r.json()),
    ])
      .then(([statsData, sitesData, deploymentsData]) => {
        setStats(statsData);
        setSites(sitesData.sites || []);
        setDeployments(deploymentsData.deployments || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error loading dashboard:', err);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const statusColors: Record<string, string> = {
    ACTIVE: 'text-green-400 bg-green-400/10',
    MAINTENANCE: 'text-yellow-400 bg-yellow-400/10',
    DISABLED: 'text-gray-400 bg-gray-400/10',
    DEPLOYING: 'text-blue-400 bg-blue-400/10',
    SUCCESS: 'text-green-400 bg-green-400/10',
    FAILED: 'text-red-400 bg-red-400/10',
    RUNNING: 'text-blue-400 bg-blue-400/10',
    PENDING: 'text-yellow-400 bg-yellow-400/10',
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-white mb-8">Dashboard</h1>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-primary-500/10">
              <Globe className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Total Sites</p>
              <p className="text-2xl font-bold text-white">{stats?.sites || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-green-500/10">
              <Rocket className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Total Deployments</p>
              <p className="text-2xl font-bold text-white">
                {stats?.totalDeployments || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-blue-500/10">
              <Clock className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Active Deployments</p>
              <p className="text-2xl font-bold text-white">
                {stats?.activeDeployments || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Sites list */}
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Sites</h2>
            <Link
              to="/sites"
              className="text-sm text-primary-400 hover:text-primary-300"
            >
              View all
            </Link>
          </div>
          <div className="space-y-3">
            {sites.slice(0, 5).map((site) => (
              <Link
                key={site.id}
                to={`/sites/${site.id}`}
                className="flex items-center justify-between p-3 rounded-lg bg-gray-700/50 hover:bg-gray-700 transition-colors"
              >
                <div>
                  <p className="font-medium text-white">{site.name}</p>
                  <p className="text-sm text-gray-400">{site.domain}</p>
                </div>
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    statusColors[site.status] || 'text-gray-400 bg-gray-400/10'
                  }`}
                >
                  {site.status}
                </span>
              </Link>
            ))}
            {sites.length === 0 && (
              <p className="text-gray-500 text-center py-4">No sites yet</p>
            )}
          </div>
        </div>

        {/* Recent deployments */}
        <div className="bg-gray-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">
              Recent Deployments
            </h2>
            <Link
              to="/deployments"
              className="text-sm text-primary-400 hover:text-primary-300"
            >
              View all
            </Link>
          </div>
          <div className="space-y-3">
            {deployments.map((deployment) => (
              <div
                key={deployment.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-gray-700/50"
              >
                {deployment.status === 'SUCCESS' ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : deployment.status === 'FAILED' ? (
                  <AlertCircle className="w-5 h-5 text-red-400" />
                ) : (
                  <Clock className="w-5 h-5 text-blue-400" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white truncate">
                    {deployment.site.name}
                  </p>
                  <p className="text-sm text-gray-400">
                    {deployment.triggeredBy} •{' '}
                    {new Date(deployment.createdAt).toLocaleString()}
                  </p>
                </div>
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    statusColors[deployment.status] ||
                    'text-gray-400 bg-gray-400/10'
                  }`}
                >
                  {deployment.status}
                </span>
              </div>
            ))}
            {deployments.length === 0 && (
              <p className="text-gray-500 text-center py-4">
                No deployments yet
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
